import type {
  GetApiV1ThreeLetterCodePartnerAdminsClientFeatureFlags200,
  GetApiV1ThreeLetterCodePartnerAdminsGroups200,
  GetApiV1ThreeLetterCodePartnerAdminsPartnerFeatureFlags200,
  GetApiV1ThreeLetterCodePublicPartnerBranches200,
  GetApiV1ThreeLetterCodePublicPrefectures200,
} from '@fukuri-admins/apis/client/gen/model';
import { UseFormReturn, useForm, useNavigate } from '@fukuri-admins/hooks/common';
import { useToast } from '@fukuri-admins/ui/common/Toast/hooks';
import { getThreeLetterCode } from '@fukuri-admins/utils/common/cookies';
import { zodResolver } from '@hookform/resolvers/zod';
import { useGetPartnerAdminsGroups, useGetPartnerAdminsPartnerFeatureFlags } from '~/hooks';
import { getClientLink } from '~/utils/getLinks';
import { generateRequestBody } from '../logics/generateRequestBody';
import { type CreateClientFormFieldValues, createClientFormSchema } from '../logics/schema';
import { CreateClientFormPresentation } from '../presentations';
import { useGetPartnerAdminsClientFeatureFlags } from './useGetPartnerAdminsClientFeatureFlags';
import { useGetPublicPartnerBranches } from './useGetPublicPartnerBranches';
import { useGetPublicPrefectures } from './useGetPublicPrefectures';
import { usePostPartnerAdminsClients } from './usePostPartnerAdminsClients';

export { useGetPublicPartnerBranches } from './useGetPublicPartnerBranches';
export { useGetPublicPrefectures } from './useGetPublicPrefectures';

type PresentationProps = React.ComponentProps<typeof CreateClientFormPresentation>;

type IUseCreateClientForm = () => {
  state: {
    methods: UseFormReturn<CreateClientFormFieldValues>;
    isButtonDisabled: boolean;
    prefectures: GetApiV1ThreeLetterCodePublicPrefectures200;
    partnerBranches: GetApiV1ThreeLetterCodePublicPartnerBranches200;
    groups: GetApiV1ThreeLetterCodePartnerAdminsGroups200;
    clientFeatureFlags: GetApiV1ThreeLetterCodePartnerAdminsClientFeatureFlags200;
    partnerFeatureFlags: GetApiV1ThreeLetterCodePartnerAdminsPartnerFeatureFlags200;
  };
  actions: {
    handleOnSubmit: PresentationProps['onSubmit'];
    handleCancel: PresentationProps['onCancel'];
  };
};

/**
 * 利用企業作成フォームのカスタムフック（新版）
 *
 * @returns state.methods - react-hook-form のメソッド
 * @returns state.isButtonDisabled - ボタン無効化フラグ
 * @returns state.prefectures - 都道府県一覧（public エンドポイント）
 * @returns state.partnerBranches - 取引店舗一覧（public エンドポイント）
 * @returns state.groups - グループ一覧
 * @returns state.clientFeatureFlags - クライアント機能フラグ一覧
 * @returns state.partnerFeatureFlags - パートナー機能フラグ一覧
 * @returns actions.handleOnSubmit - フォーム送信ハンドラ
 * @returns actions.handleCancel - キャンセルハンドラ
 */
export const useCreateClientForm: IUseCreateClientForm = () => {
  const navigate = useNavigate();
  const threeLetterCode = getThreeLetterCode();
  const { showToast } = useToast();

  const { data: prefecturesData } = useGetPublicPrefectures({ threeLetterCode });
  const { data: partnerBranchesData } = useGetPublicPartnerBranches({ threeLetterCode });
  const { data: groupsData } = useGetPartnerAdminsGroups({ threeLetterCode });
  const { data: clientFeatureFlagsData } = useGetPartnerAdminsClientFeatureFlags({
    threeLetterCode,
  });
  const { data: partnerFeatureFlagsData } = useGetPartnerAdminsPartnerFeatureFlags({
    threeLetterCode,
  });

  const methods = useForm<CreateClientFormFieldValues>({
    defaultValues: {
      name: '',
      nameKana: '',
      representativeName: '',
      corporateNumber: '',
      externalClientId: '',
      groupId: undefined,
      postalCode: '',
      prefectureCode: '',
      city: '',
      streetAddress: '',
      employeeCount: undefined,
      partnerBranchId: '',
      clientFeatureFlags: [],
      contactPersonName: '',
      contactPersonNameKana: '',
      contactPersonEmail: '',
      contactPersonPhone: '',
      contactPreferredTime: '',
      billingContactName: '',
      billingContactNameKana: '',
      billingEmail: '',
    },
    criteriaMode: 'all',
    mode: 'onTouched',
    resolver: zodResolver(
      // 取引店舗の選択肢が0件のパートナーでは partnerBranchId を任意項目として扱う（PEN-890）
      createClientFormSchema({
        isPartnerBranchRequired: partnerBranchesData.partner_branches.length > 0,
      })
    ),
  });

  const { trigger, isMutating } = usePostPartnerAdminsClients(threeLetterCode, {
    onSuccess: ({ client: { id, name } }) => {
      showToast({
        message: `「${name}」を追加しました。`,
        status: 'success',
      });
      navigate(getClientLink(id));
    },
    onError: error => {
      if (error.response?.status === 422) {
        const errors = error.response?.data?.errors;
        showToast({
          message: errors?.[0] ?? '利用企業の追加に失敗しました。',
          status: 'danger',
        });
        return;
      }

      showToast({
        message: '利用企業の追加に失敗しました。',
        status: 'danger',
      });
    },
  });

  /**
   * フォームを送信する
   */
  const handleOnSubmit: PresentationProps['onSubmit'] = methods.handleSubmit(async formValues => {
    const body = generateRequestBody({
      formValues,
      featureFlags: clientFeatureFlagsData.client_feature_flags,
    });
    await trigger(body);
  });

  /**
   * キャンセルボタンをクリックして利用企業一覧に戻る
   */
  const handleCancel: PresentationProps['onCancel'] = () => {
    navigate('/clients');
  };

  const isButtonDisabled =
    methods.formState.isSubmitting || !methods.formState.isValid || isMutating;

  return {
    state: {
      methods,
      isButtonDisabled,
      prefectures: prefecturesData,
      partnerBranches: partnerBranchesData,
      groups: groupsData,
      clientFeatureFlags: clientFeatureFlagsData,
      partnerFeatureFlags: partnerFeatureFlagsData,
    },
    actions: {
      handleOnSubmit,
      handleCancel,
    },
  };
};
